import { useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useSiteDataLayerEnabled } from '~/hooks/useSiteDataLayerEnabled';
import layerStore from '~/store2/layers';
import { SITE_DATA_LAYER_IDS, shouldRenderSiteDataLayer } from '~/store2/siteDataLayers';
import { FieldNotesLayers } from '../fieldNotes';
import FieldNoteViewer from '../fieldNotes/FieldNoteViewer';
import fieldNotesStore from '../fieldNotes/store';

/**
 * Project-scoped "Site Data → Field Notes" map layer. Mounted in Surface (independent of
 * the active tab); renders the shared {@link FieldNotesLayers} (markers + map-click → detail
 * viewer) when the layer is toggled on, EXCEPT on the Field Notes tab itself, whose own
 * MapChildren already renders the same source. No side-grid (that's tab-only) — clicking a
 * note opens the detail viewer modal. Uses the same singleton field-notes store as the tab.
 *
 * `interactive={false}` draws the markers only: no map-click handler and no viewer modal.
 */
export default function FieldNotesLayer({
  projectId,
  interactive = true,
}: {
  projectId: string;
  interactive?: boolean;
}) {
  const { tabId } = useParams({ strict: false }) as { tabId?: string };
  const { visibleLayerIds } = useSnapshot(layerStore) as typeof layerStore;
  const { fieldNotes, hoveredItem } = useSnapshot(fieldNotesStore) as typeof fieldNotesStore;
  const siteDataLayerEnabled = useSiteDataLayerEnabled();

  const active = shouldRenderSiteDataLayer(
    visibleLayerIds,
    SITE_DATA_LAYER_IDS.fieldNotes,
    tabId,
    siteDataLayerEnabled,
  );

  // Clear the open note on unmount; a leftover detailItem reopens the viewer next mount.
  useEffect(() => {
    if (!interactive) return;
    return () => fieldNotesStore.setDetailItem(null);
  }, [interactive]);

  useEffect(() => {
    if (!active) return;
    if (fieldNotesStore.projectId !== projectId) {
      fieldNotesStore.fetchFieldNotes(projectId);
    } else {
      fieldNotesStore.refreshFieldNotes(projectId);
    }
  }, [active, projectId]);

  if (!active) return null;

  return (
    <>
      <FieldNotesLayers fieldNotes={fieldNotes} hoveredItem={hoveredItem} interactive={interactive} />
      {interactive && <FieldNoteViewer />}
    </>
  );
}
